export interface ReporterContext {
  displayName: string | null;
  priorReportCount: number;
}

export interface Report {
  id: string;
  communityId: string;
  targetType: "listing" | "safety_note" | "user";
  targetId: string;
  reporterUserId: string;
  reason: string;
  detail: string;
  resolved: boolean;
  createdAt: string;
  // Only set on /admin/reports (the extended list). Older flows (e.g. the
  // per-content reports list inside Content.tsx) don't carry this.
  reporter?: ReporterContext;
}

export interface ContentSnapshot {
  kind: "listing" | "safety_note";
  id: string;
  communityId: string;
  title: string;
  body: string;
  authorUserId: string;
  hidden: boolean;
  createdAt: string;
}

export interface MenuItem {
  id: string;
  restaurantId: string;
  nameEn: string;
  nameEs: string;
  price: string;
  category: string;
  note: string | null;
  position: number;
}

export type RestaurantStatus =
  | "private"
  | "pending"
  | "approved"
  | "rejected"
  | "hidden";

export interface Restaurant {
  id: string;
  communityId: string;
  ownerUserId: string;
  status: RestaurantStatus;
  name: string;
  whatsappNumber: string;
  hours: string;
  info: string;
  // Canonical list of menu image links (may be empty). Send `menu_image_urls`
  // on write — see RestaurantForm.save().
  menuImageUrls: string[];
  // @deprecated — equals `menuImageUrls[0] ?? null`. Still returned by the API
  // for backward compat, but new code reads/writes `menuImageUrls`. Do not send
  // `menu_image_url` on write.
  menuImageUrl: string | null;
  parentRestaurantId: string | null;
  createdAt: string;
  updatedAt: string;
  menuItems: MenuItem[];
}

export interface Pagination {
  limit: number;
  offset: number;
  total: number;
}

export type ListingCategory =
  | "transport"
  | "delivery"
  | "events"
  | "where_to_buy";

export const LISTING_CATEGORIES: ListingCategory[] = [
  "transport",
  "delivery",
  "events",
  "where_to_buy",
];

export interface Listing {
  id: string;
  communityId: string;
  category: ListingCategory;
  title: string;
  body: string;
  structured: Record<string, unknown>;
  authorUserId: string;
  hidden: boolean;
  createdAt: string;
}

export type SafetyNoteCategory =
  | "scam_pattern"
  | "transport_hazard"
  | "water_hazard"
  | "general";

export const SAFETY_NOTE_CATEGORIES: SafetyNoteCategory[] = [
  "scam_pattern",
  "transport_hazard",
  "water_hazard",
  "general",
];

export interface SafetyNote {
  id: string;
  communityId: string;
  category: SafetyNoteCategory;
  patternTitle: string;
  patternDescription: string;
  placeDescription: string;
  structured: Record<string, unknown>;
  authorUserId: string;
  hidden: boolean;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// AI-assist response shapes (expat-api #3065).
//
// Mirrors the server contract: POST /admin/parse-menu (Claude vision) and
// POST /admin/resolve-place (Google Places). Kept in sync with
// expat-api/src/ai/anthropic.ts and expat-api/src/ai/places.ts — do NOT
// reshape these from local guesses; they match the documented endpoint.
//
// Both endpoints are stateless draft generators: they write nothing to the DB.
// The admin reviews/corrects the draft in RestaurantForm and saves via the
// existing POST/PUT /admin/restaurants endpoints.
// ---------------------------------------------------------------------------

// POST /admin/parse-menu → { items: ParsedMenuItem[]; warnings: string[] }
// Accepts a menu image OR a (possibly multi-page) PDF.
// Note: `price` arrives as a number|null from the parser (the form edits it as
// a free-text string, so we stringify on intake).
export interface ParsedMenuItem {
  name_es: string;
  name_en: string;
  price: number | null;
  category: string | null;
  note: string | null;
  // Per-row parse caveat tied to THIS item (price smudged, text unclear, …).
  // null when the row parsed cleanly. Surfaced inline on the menu-editor row.
  warning: string | null;
}

export interface ParseMenuResponse {
  items: ParsedMenuItem[];
  // Document-level warnings only (glare, a page that failed to render, …).
  // Per-row caveats live on ParsedMenuItem.warning.
  warnings: string[];
}

// POST /admin/resolve-place → union of { resolved } | { candidates }.
export interface ResolvedPlaceOpeningHours {
  weekday_text: string[];
  periods: unknown[];
  open_now: boolean | null;
}

export interface ResolvedPlace {
  name: string | null;
  formatted_address: string | null;
  opening_hours: ResolvedPlaceOpeningHours | null;
  formatted_phone_number: string | null;
  lat: number | null;
  lng: number | null;
  place_id: string;
}

export interface PlaceCandidate {
  name: string;
  formatted_address: string | null;
  place_id: string;
  lat: number | null;
  lng: number | null;
}

// Mutually-exclusive shapes — branch on which property is present.
export type ResolvePlaceResponse =
  | { resolved: ResolvedPlace }
  | { candidates: PlaceCandidate[] };

export interface AdminCommunitySummary {
  id: string;
  slug: string;
  name: string;
  active: boolean;
  createdAt: string;
  pendingRestaurantCount: number;
  openReportCount: number;
}
